import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('click', this.#handleCheckoutClick, { capture: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('click', this.#handleCheckoutClick, { capture: true });
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /**
   * Closes the drawer as soon as the checkout button is clicked.
   *
   * The drawer is a native `<dialog>` opened with `showModal()`, so it lives in the browser's
   * top layer and paints above everything else on the page. Third-party checkout overlays
   * (Razorpay Magic Checkout) are plain DOM nodes, so they render underneath the open drawer.
   * The close skips the slide-out animation, otherwise the drawer would sit over the overlay
   * for the length of it.
   *
   * The close is queued as a microtask so it lands after the click's activation behaviour - the
   * cart form submit the checkout provider hooks into - has already been triggered. That still
   * runs before the next paint, so the drawer visually disappears on the same frame as the
   * click, but the submit can't be disturbed by the dialog closing mid-dispatch.
   *
   * Bound on `document` in the capture phase, not on the button: the cart contents are
   * re-rendered on update, and the checkout provider's own script may stop propagation of the
   * click before it bubbles. Capture on `document` runs ahead of both.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleCheckoutClick = (event) => {
    const { target } = event;
    if (!(target instanceof Element)) return;
    if (!target.closest('button[name="checkout"], #checkout')) return;
    if (!this.refs.dialog?.open) return;

    queueMicrotask(() => this.closeDialogInstantly());
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
